/**
 * Centralized Game Configuration & Tuning Parameters
 */

export const GAME_CONFIG = {
    // ── Player Tuning Parameters ─────────────────────────────────────────
    player: {
        maxHp: 100,
        baseSpeed: 150,
        boostedSpeed: 240,
        lungeForce: 320,
        lungeDecay: 0.82,
        biteDamage: 35,
        biteCooldown: 0.45,
        biteInvulnerabilityDuration: 0.08,
        biteReach: 34,
        biteRadius: 28,
        barkKnockback: 220,
        barkCooldown: 2,
        barkArcAngle: 360,   // Full 360° radial bark shockwave
        barkRadius: 70,     // Smaller shockwave radius (pixels)
        barkDuration: 0.45,  // Slower expansion duration (seconds)
    },

    // ── AI Tuning Parameters ─────────────────────────────────────────────
    humanAI: {
        visionRadius: 220,
        visionConeAngle: 75,
        patrolSpeed: 40,
        turnSpeed: 220,
        alarmPauseTime: 1.5,
        investigatePauseTime: 0.45,
        fleeSpeed: 180,
        alertCalmTime: 5,
    },

    guardRobot: {
        maxHp: 60,
        speed: 95,
        attackDamage: 15,
        attackCooldown: 0.8,
        attackKnockback: 500,  // Knockback force applied to player on attack
        meleeRange: 14,        // Center distance for contact-range damage
        shootRange: 150,
        shootCooldown: 1.6,
        pelletSpeed: 260,
        trapDuration: 0.5,
        trapDamage: 5,
        repathInterval: 0.30,  // A* path recalculation interval (seconds)
        stuckThreshold: 0.1,   // Stuck detection threshold time (seconds)
    },

    // ── Sniff / Scent Mark Tuning ────────────────────────────────────────
    sniff: {
        radius: 240,
        cooldown: 5,
        fadeDuration: 5,
    },

    // ── Environmental Lighting Tuning Parameters ─────────────────────────
    lighting: {
        // Dimmer base wash (rooms with no lamps rely on this + dog glow)
        ambientColor: "#3a4458",
        unloadedColor: "#000000",
        // Per-region ambient overrides (Regions id from flood-fill order)
        ambientByRoomId: {
            // R2 tunnel — darker so the dog glow reads as the light source
            2: "#1c2230",
        },
        lampRadius: 130,
        terminalRadius: 100,
        doorLedRadius: 50,
        // Player eye / body glow (personal light)
        dogGlow: {
            color: [70, 190, 255],
            radius: 78,
            intensity: 0.72,
            actionColor: [255, 35, 35],
            actionRadius: 42,
            actionIntensity: 0.55,
        },
        // Light-layer tile GID → lamp style (paint different tiles for colour)
        lightByGid: {
            default: { color: [0, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // wall lamps
            11: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // green fish tank
            15: { color: [0, 255, 0], radius: 130, intensity: 0.9, flicker: true },
            16: { color: [0, 255, 0], radius: 130, intensity: 0.9, flicker: true },
            // wall lamps
            19: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // table lamp
            97: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // terminal`
            24: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // ceiling lamp
            219: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // street lamp
            114: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            115: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            116: { color: [255, 224, 150], radius: 130, intensity: 0.9, flicker: true },
            // alarm strobe — off until humans/robots alerted in its region
            242: {
                color: [255, 36, 36],
                radius: 140,
                intensity: 0,
                alarm: true,
                alarmIntensity: 1.0,
                pulse: true,
                flicker: true,
            },
            243: {
                color: [255, 36, 36],
                radius: 140,
                intensity: 0,
                alarm: true,
                alarmIntensity: 1.0,
                pulse: true,
                flicker: true,
            },
        },
    },

    // ── Room Fog (only current Regions room; crossfade on enter) ─────────
    rooms: {
        // Seconds to fade ambient/lamps in or out when changing region
        fadeDuration: 0.45,
        // When standing in a Regions gap (doorway), nearby rooms stay this bright
        gapAmbient: 0.25,
        // How far from a room edge (px) still counts as "adjacent" while in a gap
        gapAdjacentDistance: 96,
    },

    // ── Audio (positional SFX over KAPLAY pan/volume) ────────────────────
    audio: {
        maxVolume: 1,
        uiVolume: 0.7,
        musicVolume: 0.28,
        alarmVolume: 0.55,
        // Full volume within near (px); silent at/beyond far
        near: 48,
        far: 420,
        // Stereo: full L/R when |dx| >= panScale
        panScale: 220,
        // Alarm / attached loops duck over this many seconds when gameplay pauses
        pauseFadeSeconds: 0.35,
        // Random takes inside public/sounds/sfx_bark.mp3 (silence-gated)
        barkSlices: [
            { seek: 0.49, duration: 0.60 },
            { seek: 1.19, duration: 0.60 },
            { seek: 1.94, duration: 0.60 },
            { seek: 2.69, duration: 0.60 },
            { seek: 3.30, duration: 0.60 },
            { seek: 4.53, duration: 0.60 },
            { seek: 5.26, duration: 0.60 },
            { seek: 5.96, duration: 0.60 },
        ],
    },

    // ── Debug ────────────────────────────────────────────────────────────
    debug: {
        hud: false,
        collisionBounds: true,
        // Profiler rides along with the backtick HUD (`). Keys: 1 = toggle
        // lighting, p = dump timings to the browser console.
    },

    // ── Level Geometry Tuning Parameters ─────────────────────────────────
    level: {
        tileWidth: 32,
        tileHeight: 32,
    },
};
